import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, Upload, Check, X, Calendar } from 'lucide-react';
import Header from '../components/Header';
import MainTabsNav from '../components/MainTabsNav';
import Footer from '../components/Footer';
import AdminSidebar from '../components/AdminSidebar';
import { useAuthStore } from '../stores/authStore';
import { exportToExcel, importFromExcel } from '../utils/excelUtils';

interface EnrollmentWithApproval {
  id: string;
  short_id?: string; // 6자리 축소판 ID (엑셀용)
  user_id: string;
  class_id: string;
  status: 'ACTIVE' | 'CANCELLED';
  certificate_approved: boolean;
  certificate_approved_at: string | null;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
    email: string;
    name?: string;
    phone?: string;
  };
}

export default function CertificateApprovalPage() {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [enrollments, setEnrollments] = useState<EnrollmentWithApproval[]>([]);
  const [classTitle, setClassTitle] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [issuedDate, setIssuedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // YYYY-MM-DD
  });

  useEffect(() => {
    if (!user || (user.role !== 'provider' && user.role !== 'admin')) {
      navigate('/');
      return;
    }
    if (!classId) {
      navigate('/provider/posts');
      return;
    }
    fetchEnrollments();
    fetchClassInfo();
  }, [user, classId, navigate]);

  const fetchClassInfo = async () => {
    try {
      const response = await fetch(`/api/v1/classes/${classId}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setClassTitle(data.class?.title || '수업');
      }
    } catch (error) {
      console.error('Failed to fetch class info:', error);
    }
  };

  const fetchEnrollments = async () => {
    try {
      const response = await fetch(`/api/v1/provider/classes/${classId}/certificate-approvals`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('수강 신청 목록을 불러오는데 실패했습니다.');
      }

      const data = await response.json();
      setEnrollments(data.enrollments || []);
    } catch (error: any) {
      console.error('Failed to fetch enrollments:', error);
      alert(error.message || '수강 신청 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalToggle = async (enrollmentId: string, currentApproved: boolean) => {
    try {
      setUpdating(true);
      const response = await fetch(
        `/api/v1/provider/classes/${classId}/certificate-approvals/${enrollmentId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ approved: !currentApproved }),
        }
      );

      if (!response.ok) {
        throw new Error('승인 상태 업데이트에 실패했습니다.');
      }

      // Update local state
      setEnrollments((prev) =>
        prev.map((e) =>
          e.id === enrollmentId
            ? { ...e, certificate_approved: !currentApproved }
            : e
        )
      );
    } catch (error: any) {
      console.error('Failed to update approval:', error);
      alert(error.message || '승인 상태 업데이트에 실패했습니다.');
    } finally {
      setUpdating(false);
    }
  };

  const handleExport = () => {
    exportToExcel(enrollments, classTitle);
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUpdating(true);
      const approvals = await importFromExcel(file);

      // Bulk update API call
      const response = await fetch(
        `/api/v1/provider/classes/${classId}/certificate-approvals/bulk`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ approvals }),
        }
      );

      if (!response.ok) {
        throw new Error('일괄 승인 상태 업데이트에 실패했습니다.');
      }

      alert('승인 상태가 업데이트되었습니다.');
      fetchEnrollments(); // Refresh list
    } catch (error: any) {
      console.error('Failed to import:', error);
      alert(error.message || '파일 처리 중 오류가 발생했습니다.');
    } finally {
      setUpdating(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleFinalApproval = () => {
    setShowConfirmModal(false);
    // 결제 페이지로 이동 (결제 완료 후 인증서 자동 발급)
    navigate(`/payment?type=certificate_issue&classId=${classId}&issuedAt=${issuedDate}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const allApproved = enrollments.length > 0 && enrollments.every((e) => e.certificate_approved);
  const approvedCount = enrollments.filter((e) => e.certificate_approved).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <MainTabsNav className="mt-16" />
        <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
          <AdminSidebar />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">로딩 중...</p>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <MainTabsNav className="mt-16" />
      <div className="flex flex-1 max-w-[1440px] mx-auto w-full">
        <AdminSidebar />
        <main className="flex-1 min-w-0 p-6 sm:px-10 lg:px-16">
          <div>
            {/* Page Header */}
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">인증서 발급 관리</h1>
                <p className="text-gray-600">{classTitle}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleExport}
                  disabled={enrollments.length === 0}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <Download className="w-5 h-5" />
                  엑셀 내보내기
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleImport}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={updating}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <Upload className="w-5 h-5" />
                  엑셀 불러오기
                </button>
              </div>
            </div>

            {/* Enrollments Table */}
            {enrollments.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <p className="text-gray-600 text-lg">수강 신청자가 없습니다.</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          이름
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          이메일
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          연락처
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                          신청일
                        </th>
                        <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                          승인
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {enrollments.map((enrollment) => (
                        <tr key={enrollment.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {enrollment.user?.name || '이름 없음'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {enrollment.user?.email}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {enrollment.user?.phone || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {formatDate(enrollment.created_at)}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() =>
                                handleApprovalToggle(
                                  enrollment.id,
                                  enrollment.certificate_approved
                                )
                              }
                              disabled={updating}
                              className={`inline-flex items-center justify-center w-8 h-8 rounded transition-all ${
                                enrollment.certificate_approved
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                              title={
                                enrollment.certificate_approved ? '승인 취소' : '승인하기'
                              }
                            >
                              {enrollment.certificate_approved ? (
                                <Check className="w-5 h-5" />
                              ) : (
                                <X className="w-5 h-5" />
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Summary */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">
                      총 {enrollments.length}명 / 승인 {approvedCount}명
                    </span>
                    {allApproved && (
                      <span className="text-green-600 font-semibold">
                        ✓ 모든 수강생이 승인되었습니다
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Final Approval Button */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setShowConfirmModal(true)}
                disabled={!allApproved || issuing}
                className={`px-8 py-3 rounded-lg font-semibold transition-colors ${
                  allApproved && !issuing
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                title={
                  allApproved
                    ? '인증서 최종 발급'
                    : '모든 수강생을 승인해야 최종 발급이 가능합니다'
                }
              >
                {issuing ? '발급 중...' : '최종 승인 완료'}
              </button>
            </div>

            {/* Confirmation Modal */}
            {showConfirmModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    인증서 최종 발급
                  </h3>
                  <p className="text-gray-600 mb-4">
                    정말로 {enrollments.length}명의 수강생에게 인증서를 발급하시겠습니까?
                  </p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="w-4 h-4 inline-block mr-1" />
                      인증서 발급 일자
                    </label>
                    <input
                      type="date"
                      value={issuedDate}
                      onChange={(e) => setIssuedDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-blue-800">
                      발급 비용: {approvedCount} x 1,000원 = {(approvedCount * 1000).toLocaleString()}원
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      결제 완료 후 인증서가 자동으로 발급됩니다.
                    </p>
                  </div>
                  <p className="text-sm text-red-600 mb-6">
                    ⚠️ 발급 후에는 취소할 수 없습니다.
                  </p>
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setShowConfirmModal(false)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleFinalApproval}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      확인
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </div>
  );
}
